import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { CheckCircle2, MessageCircle } from 'lucide-react'
import VoiceSectionPage from '@/components/layout/VoiceSectionPage'
import { VOICE_EXTRA_NUMBER_BASE_BRL, VOICE_PRICING_HELP } from '@/config/voicePricing'
import { getVoicePlanById, voicePlans } from '@/config/voicePlans'
import { useAuth } from '@/contexts/AuthContext'
import { supabase } from '@/supabaseClient'

const BASE_MINUTE_COSTS = {
  mobile: 0.08,
  fixed: 0.04,
}

const MARGIN_MULTIPLIER = 1.4 // custo + 40%
const INCLUDED_CHANNELS = 2

const CONTACT_URL =
  import.meta.env.VITE_WAPPDESK_CHAT_URL ||
  import.meta.env.VITE_WAPPDESK_WHATSAPP_URL ||
  'https://wa.me/553131577830'

function toBRL(value) {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0)
}

function toMinutePrice(value) {
  return toBRL(value).replace(' ', ' ')
}

export default function PlanosPage() {
  const navigate = useNavigate()
  const { getProfileId } = useAuth()
  const profileId = getProfileId()
  const [billingCycle, setBillingCycle] = useState('monthly')
  const [mobileMinutes, setMobileMinutes] = useState(300)
  const [fixedMinutes, setFixedMinutes] = useState(300)
  const [totalNumbers, setTotalNumbers] = useState(1)
  const [ownedActiveNumbers, setOwnedActiveNumbers] = useState(0)
  const [paidNumbersInMonth, setPaidNumbersInMonth] = useState(0)
  const [loadingOwnedNumbers, setLoadingOwnedNumbers] = useState(true)
  const plans = voicePlans

  const prices = useMemo(() => {
    const mobileMinutePrice = BASE_MINUTE_COSTS.mobile * MARGIN_MULTIPLIER
    const fixedMinutePrice = BASE_MINUTE_COSTS.fixed * MARGIN_MULTIPLIER
    const variableUsage = mobileMinutes * mobileMinutePrice + fixedMinutes * fixedMinutePrice

    return {
      mobileMinutePrice,
      fixedMinutePrice,
      variableUsage,
    }
  }, [fixedMinutes, mobileMinutes, totalNumbers])

  const estimatesByPlan = useMemo(() => {
    return plans.reduce((acc, plan) => {
      const included = plan.includedNumbers ?? 1
      const extraPrice = plan.extraNumberPrice ?? VOICE_EXTRA_NUMBER_BASE_BRL
      const extraCount = Math.max(0, totalNumbers - included)
      const extraNumbersMonthly = extraCount * extraPrice
      const planMonthly = plan.monthlyPrice
      const planAnnual = planMonthly == null ? null : planMonthly * 10 // 2 meses grátis
      const finalMonthly = planMonthly == null ? null : planMonthly + extraNumbersMonthly + prices.variableUsage
      const finalAnnual = planAnnual == null ? null : planAnnual + (extraNumbersMonthly + prices.variableUsage) * 12
      const transitionCredit = Math.min(
        planMonthly ?? 0,
        (plan.extraNumberPrice ?? VOICE_EXTRA_NUMBER_BASE_BRL) *
        Math.min(plan.includedNumbers ?? 0, paidNumbersInMonth)
      )
      const firstChargeMonthly = planMonthly == null ? null : Math.max(0, planMonthly - transitionCredit)

      acc[plan.id] = {
        extraCount,
        extraNumbersMonthly,
        planAnnual,
        finalMonthly,
        finalAnnual,
        transitionCredit,
        firstChargeMonthly,
      }
      return acc
    }, {})
  }, [paidNumbersInMonth, prices.variableUsage, totalNumbers])

  useEffect(() => {
    async function loadOwnedNumbers() {
      if (!profileId) {
        setOwnedActiveNumbers(0)
        setPaidNumbersInMonth(0)
        setLoadingOwnedNumbers(false)
        return
      }

      setLoadingOwnedNumbers(true)
      const monthStart = new Date()
      monthStart.setDate(1)
      monthStart.setHours(0, 0, 0, 0)

      const { data, error } = await supabase
        .from('whatsapp_numbers')
        .select('id, status, created_at')
        .eq('profile_id', profileId)
        .in('status', ['active', 'grace'])

      if (error) {
        console.error('[PlanosPage] whatsapp_numbers:', error)
        setOwnedActiveNumbers(0)
        setPaidNumbersInMonth(0)
      } else {
        const rows = data || []
        setOwnedActiveNumbers(rows.length)
        const paidInMonth = rows.filter((r) => {
          if (!r.created_at) return false
          return new Date(r.created_at) >= monthStart
        }).length
        setPaidNumbersInMonth(paidInMonth)
      }
      setLoadingOwnedNumbers(false)
    }

    loadOwnedNumbers()
  }, [profileId])

  function openEnterpriseContact() {
    window.open(CONTACT_URL, '_blank', 'noopener,noreferrer')
  }

  function openSummary(planId) {
    const plan = getVoicePlanById(planId)
    if (!plan) return
    if (plan.id === 'enterprise') {
      openEnterpriseContact()
      return
    }
    navigate('/planos/resumo', { state: { planId, billingCycle } })
  }

  return (
    <VoiceSectionPage
      title="Contratar planos"
      description="Planos com cobrança mensal no cartão, incluindo 1 número com até 2 canais sem custo extra."
    >
      <div className="space-y-6">
        <section className="rounded-xl border border-border bg-muted/10 p-4 shadow-sm">
          <p className="text-sm font-semibold text-foreground">Preço por número adicional ou avulso</p>
          <ul className="mt-2 space-y-1 text-sm text-muted-foreground">
            <li>
              <span className="font-medium text-foreground">{VOICE_PRICING_HELP.baseLabel}:</span>{' '}
              {toBRL(VOICE_PRICING_HELP.baseValue)}/mês
            </li>
            <li>
              <span className="font-medium text-foreground">{VOICE_PRICING_HELP.proLabel}:</span>{' '}
              {toBRL(VOICE_PRICING_HELP.proValue)}/mês
            </li>
          </ul>
        </section>

        <section className="rounded-xl border border-border bg-card p-5 shadow-sm">
          <h2 className="text-lg font-semibold text-foreground">Simulador de tarifação</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Ajuste os parâmetros e compare o valor final em cada plano abaixo.
          </p>

          <div className="mt-5 grid gap-4 lg:grid-cols-3">
            <label className="flex flex-col gap-2 rounded-lg border border-border p-3">
              <span className="text-sm font-medium text-foreground">Minutos móveis/mês</span>
              <input
                type="range"
                min="0"
                max="5000"
                step="10"
                value={mobileMinutes}
                onChange={(e) => setMobileMinutes(Number(e.target.value))}
              />
              <span className="text-sm text-muted-foreground">{mobileMinutes} min</span>
            </label>

            <label className="flex flex-col gap-2 rounded-lg border border-border p-3">
              <span className="text-sm font-medium text-foreground">Minutos fixos/mês</span>
              <input
                type="range"
                min="0"
                max="5000"
                step="10"
                value={fixedMinutes}
                onChange={(e) => setFixedMinutes(Number(e.target.value))}
              />
              <span className="text-sm text-muted-foreground">{fixedMinutes} min</span>
            </label>

            <label className="flex flex-col gap-2 rounded-lg border border-border p-3">
              <span className="text-sm font-medium text-foreground">Quantidade de números</span>
              <input
                type="range"
                min="1"
                max="30"
                step="1"
                value={totalNumbers}
                onChange={(e) => setTotalNumbers(Number(e.target.value))}
              />
              <span className="text-sm text-muted-foreground">
                {totalNumbers} número(s)
              </span>
            </label>
          </div>

          <div className="mt-5 grid gap-3 sm:grid-cols-2">
            <div className="rounded-lg border border-border p-3">
              <p className="text-xs text-muted-foreground">Minuto móvel</p>
              <p className="text-lg font-semibold text-foreground">{toMinutePrice(prices.mobileMinutePrice)}</p>
            </div>
            <div className="rounded-lg border border-border p-3">
              <p className="text-xs text-muted-foreground">Minuto fixo</p>
              <p className="text-lg font-semibold text-foreground">{toMinutePrice(prices.fixedMinutePrice)}</p>
            </div>
          </div>

          <p className="mt-4 text-xs text-muted-foreground">
            Canal extra disponível somente no plano <span className="font-semibold text-foreground">Enterprise</span> (sob consulta).
          </p>
        </section>

        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-sm font-semibold text-foreground">Forma de pagamento</p>
            <p className="text-xs text-muted-foreground">No anual, você ganha 2 meses (paga 10 meses).</p>
          </div>
          <div className="inline-flex w-fit rounded-lg border border-border p-1">
            <button
              type="button"
              onClick={() => setBillingCycle('monthly')}
              className={`rounded-md px-3 py-1.5 text-sm ${
                billingCycle === 'monthly'
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              Mensal
            </button>
            <button
              type="button"
              onClick={() => setBillingCycle('annual')}
              className={`rounded-md px-3 py-1.5 text-sm ${
                billingCycle === 'annual'
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              Anual (2 meses grátis)
            </button>
          </div>
        </div>

        <ul className="grid gap-4 lg:grid-cols-3">
        {plans.map((plan) => (
          <li key={plan.id} className="relative flex h-full flex-col rounded-xl border border-border bg-card p-5 shadow-sm">
            {plan.badge ? (
              <span className="absolute -top-2 right-3 rounded-full bg-primary px-2.5 py-1 text-[11px] font-semibold text-primary-foreground shadow-sm">
                {plan.badge}
              </span>
            ) : null}
            <h2 className="text-lg font-semibold text-foreground">{plan.name}</h2>
            <p className="mt-1 text-sm text-muted-foreground">{plan.description}</p>
            <p className="mt-4 text-xl font-bold text-foreground">
              {plan.monthlyPrice == null
                ? 'Sob consulta'
                : billingCycle === 'annual'
                  ? `${toBRL(plan.monthlyPrice * 10)}/ano`
                  : `${toBRL(plan.monthlyPrice)}/mês`}
            </p>

            <ul className="mt-4 space-y-2 text-sm text-muted-foreground">
              <li className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-primary" />{' '}
                {plan.includedNumbers == null
                  ? 'Quantidade de números sob consulta'
                  : `${plan.includedNumbers} número(s) incluso(s) (até 2 canais cada)`}
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-primary" />{' '}
                {plan.extraNumberPrice == null
                  ? 'Número adicional sob consulta'
                  : `Número adicional por ${toBRL(plan.extraNumberPrice)}/mês`}
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-primary" /> Minuto móvel: {toMinutePrice(prices.mobileMinutePrice)}
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-primary" /> Minuto fixo: {toMinutePrice(prices.fixedMinutePrice)}
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-primary" /> Canal extra: somente Enterprise (sob consulta)
              </li>
            </ul>

            <div className="mt-4 rounded-lg border border-border bg-muted/20 p-3">
              <p className="text-xs text-muted-foreground">Valor estimado mensal</p>
              <p className="mt-1 text-lg font-semibold text-foreground">
                {(billingCycle === 'annual'
                  ? estimatesByPlan[plan.id]?.finalAnnual
                  : estimatesByPlan[plan.id]?.finalMonthly) == null
                  ? 'Sob consulta'
                  : billingCycle === 'annual'
                    ? `${toBRL(estimatesByPlan[plan.id].finalAnnual)}/ano`
                    : `${toBRL(estimatesByPlan[plan.id].finalMonthly)}/mês`}
              </p>
              <p className="mt-1 text-xs text-muted-foreground">
                Uso: {toBRL(prices.variableUsage)}/mês • Números adicionais: {toBRL(estimatesByPlan[plan.id]?.extraNumbersMonthly || 0)}/mês
              </p>
              <p className="mt-1 text-xs text-muted-foreground">
                Você já possui {loadingOwnedNumbers ? '...' : ownedActiveNumbers} número(s) ativo(s) • Abatimento estimado 1º ciclo:{' '}
                <span className="font-semibold text-foreground">{toBRL(estimatesByPlan[plan.id]?.transitionCredit || 0)}</span>
              </p>
              {estimatesByPlan[plan.id]?.firstChargeMonthly != null ? (
                <p className="mt-1 text-xs text-muted-foreground">
                  1ª cobrança estimada do plano: <span className="font-semibold text-foreground">{toBRL(estimatesByPlan[plan.id].firstChargeMonthly)}</span>
                </p>
              ) : null}
            </div>

            <div className="mt-auto pt-6">
              {plan.id === 'enterprise' ? (
                <button
                  type="button"
                  onClick={openEnterpriseContact}
                  className="inline-flex min-h-[44px] w-full items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground"
                >
                  <MessageCircle className="h-4 w-4" />
                  Falar com especialista
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => openSummary(plan.id)}
                  className="inline-flex min-h-[44px] w-full items-center justify-center rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground disabled:opacity-50"
                >
                  {`Continuar (${billingCycle === 'annual' ? 'anual' : 'mensal'})`}
                </button>
              )}
            </div>
          </li>
        ))}
        </ul>
      </div>
    </VoiceSectionPage>
  )
}
